import { Stack } from "@mui/material";
import type { InvalidSubjectDidErrorProps } from "./InvalidSubjectDidError.types";
import { useTranslation } from "react-i18next";
import AlertWithList from "../../molecules/AlertWithList/AlertWithList";
import { Loader } from "../CreateValidatedQuestionFilters/filters/Loader.component";
import mapInvalidSubjectDids from "@/src/lib/mapInvalidSubjectDids/mapInvalidSubjectDids";

export const InvalidSubjectDidError = ({
    conflictingSubjectDidFailures,
    missingHealthcareOfficeIriFailures,
    loading,
}: InvalidSubjectDidErrorProps) => {
    const { t } = useTranslation("common", {
        keyPrefix: "invalid-subject-did-error",
    });

    const conflictingSubjectDidCount =
        conflictingSubjectDidFailures?.length || 0;
    const missingHealthcareOfficeIriCount =
        missingHealthcareOfficeIriFailures?.length || 0;

    if (
        conflictingSubjectDidCount === 0 &&
        missingHealthcareOfficeIriCount === 0
    ) {
        return null;
    }

    if (loading)
        return (
            <Stack direction="row" py={2} justifyContent="center">
                <Loader />
            </Stack>
        );

    if (!conflictingSubjectDidFailures || !missingHealthcareOfficeIriFailures) {
        return null;
    }

    const uniqueConflictingSubjectDids = mapInvalidSubjectDids(
        conflictingSubjectDidFailures
    );

    const uniqueMissingHealthcareOfficeIris = mapInvalidSubjectDids(
        missingHealthcareOfficeIriFailures
    );

    return (
        <Stack spacing={1}>
            {loading ? (
                <Stack direction="row" py={2} justifyContent="center">
                    <Loader />
                </Stack>
            ) : null}

            {conflictingSubjectDidCount > 0 ? (
                <AlertWithList
                    title={t("conflicting-credentials", {
                        amount: conflictingSubjectDidCount,
                    })}
                    items={
                        uniqueConflictingSubjectDids?.map((failure) => ({
                            label: t("subject-did-name", {
                                subjectDidName: failure.subjectDidName,
                            }),
                            subItems: failure.indicators.map((indicator) => ({
                                label: indicator.name,
                            })),
                        })) || []
                    }
                />
            ) : null}

            {missingHealthcareOfficeIriCount > 0 ? (
                <AlertWithList
                    title={t("missing-healthcare-office-iri", {
                        amount: missingHealthcareOfficeIriCount,
                    })}
                    items={
                        uniqueMissingHealthcareOfficeIris?.map((failure) => ({
                            label: t("subject-did-name", {
                                subjectDidName: failure.subjectDidName,
                            }),
                            subItems: failure.indicators.map((indicator) => ({
                                label: indicator.name,
                            })),
                        })) || []
                    }
                />
            ) : null}
        </Stack>
    );
};
